import React, {useContext, useEffect, useState, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {formatDateTime} from "../../utils/Utils";
import {getQrScanEvents, getQrPurchaseStatus} from "./qrPurchaseApi";
import {outcomeInfo, reviewReasonsText, QR_STEP_LABELS} from "./qrReasonLabels";
import {QrScanStatusStrip} from "./QrScanStatusStrip";
import {reviewDetailsTitle} from "./qrReviewDetails";
import {QrScanPurchaseActions} from "./QrScanPurchaseActions";
import {useQrPurchaseStates} from "./useQrPurchaseStates";
import {confirmAndDiscardQrDraft} from "./qrDraftActions";

const REFRESH_MS = 10000;

function formatTons(value) {
    if (value === null || value === undefined || value === "") return "-";
    const n = parseFloat(value);
    return isNaN(n) ? "-" : `${n.toFixed(3)} t`;
}

export function QrScanLogPage() {
    const myData = useContext(BedRockContext);
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [discarding, setDiscarding] = useState(false);
    const {states: purchaseStates, resolve: resolvePurchaseStates, markMissing} = useQrPurchaseStates();

    const profileBreadCrumbs = [
        {title: "Home", path: "/dashboard", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
        {title: "Purchases", path: "/purchases-list", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
    ];

    const loadData = useCallback(async () => {
        const [eventsRes, statusRes] = await Promise.allSettled([getQrScanEvents(200), getQrPurchaseStatus()]);
        if (eventsRes.status === "fulfilled") {
            const list = Array.isArray(eventsRes.value?.data) ? eventsRes.value.data : [];
            await resolvePurchaseStates(list);
            setEvents(list);
            setLoadError(false);
        } else {
            setLoadError(true);
        }
        if (statusRes.status === "fulfilled") {
            setStatus(statusRes.value?.data || null);
            setStatusError(false);
        } else {
            setStatusError(true);
        }
        setIsLoading(false);
        setLastRefreshed(new Date());
    }, [resolvePurchaseStates]);

    useEffect(() => {
        loadData();
        const timer = setInterval(loadData, REFRESH_MS);
        return () => clearInterval(timer);
    }, [loadData]);

    useEffect(() => {
        if (myData && myData.setBaseData) {
            myData.setBaseData({
                toolBarButtons: [
                    {name: "Refresh", color: "primary", icon: "fa fa-sync", onClick: () => loadData()},
                ]
            });
        }
    }, [loadData]);

    async function discardDraft(event) {
        setDiscarding(true);
        try {
            const discarded = await confirmAndDiscardQrDraft(event.purchaseId);
            if (discarded) {
                markMissing(event.purchaseId);
                loadData();
            }
        } finally {
            setDiscarding(false);
        }
    }

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>QR Scan Log</PageTitle>
            <div className="card card-body">
                <QrScanStatusStrip status={status} statusError={statusError} lastRefreshed={lastRefreshed}/>
                {loadError && (
                    <div className="alert alert-danger py-2"><small>Could not load the scan log. Retrying…</small></div>
                )}
                <div className="table-responsive">
                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3 fs-7">
                        <thead>
                            <tr className="fw-bold text-muted bg-light">
                                <th className="ps-4 min-w-150px">Time</th>
                                <th className="min-w-120px">Vehicle</th>
                                <th className="min-w-150px">Outcome</th>
                                <th>Step</th>
                                <th className="text-end">Weight</th>
                                <th className="min-w-200px">Review reasons</th>
                                <th className="text-end pe-4">Purchase</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && events.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-muted py-10">Loading…</td></tr>
                            )}
                            {!isLoading && events.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-muted py-10">No QR scans yet.</td></tr>
                            )}
                            {events.map((event, idx) => {
                                const info = outcomeInfo(event.outcome);
                                const reasons = reviewReasonsText(event.reviewReasons);
                                return (
                                    <tr key={event.id || idx}>
                                        <td className="ps-4">{formatDateTime(event.receivedAt)}</td>
                                        <td>
                                            <span className="text-primary fw-semibold">{event.vehicleNumber || event.code || "-"}</span>
                                        </td>
                                        <td>
                                            <span className={`badge badge-light-${info.color}`}>{info.label}</span>
                                        </td>
                                        <td>{QR_STEP_LABELS[event.step] || "-"}</td>
                                        <td className="text-end">{formatTons(event.weightTons)}</td>
                                        <td title={reasons ? reviewDetailsTitle(event.reviewReasons, event.reviewNote) : undefined}>
                                            {reasons || "-"}
                                            {event.outcome === "ERROR" && event.message && (
                                                <div className="text-muted fs-8">{event.message}</div>
                                            )}
                                        </td>
                                        <td className="text-end pe-4">
                                            <QrScanPurchaseActions
                                                event={event}
                                                state={purchaseStates[event.purchaseId]}
                                                discarding={discarding}
                                                onEdit={(ev) => navigate(`/purchase-new/edit/${ev.purchaseId}`)}
                                                onView={(ev) => navigate(`/purchase-new/view/${ev.purchaseId}`)}
                                                onDiscard={discardDraft}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
